/**
 * vendorSeo.js
 * ─────────────────────────────────────────────────────────────────────────────
 * DRY helpers for vendor-first social previews and share text.
 *
 * Why this file exists
 * --------------------
 * Three storefronts (retailer/wholesaler, restaurant, healthcare) used to
 * each hand-roll their own <SEOHead /> props and their own WhatsApp/clipboard
 * share strings. The result was that vendors got inconsistent treatment when
 * their links were shared:
 *   - Retailer pages: showed the vendor banner OR the Lusuku default.
 *   - Restaurant pages: showed the vendor logo only (no banner fallback).
 *   - Healthcare pages: had no SEO tags at all — every share rendered as
 *     the generic Lusuku card.
 *
 * The helpers below normalise that surface so:
 *   1. The OG / Twitter / JSON-LD card always leads with the vendor's banner
 *      (preferred) or logo, and the vendor's name is the title.
 *   2. The schema.org Organization graph names the vendor as the org —
 *      not "Lusuku" — so search engines and AI scrapers attribute correctly.
 *   3. Lusuku-defaults still kick in when a vendor hasn't uploaded
 *      anything; nothing breaks.
 *   4. WhatsApp / clipboard share text uses the same identity logic as the
 *      OG card (vendor first, "Powered by Lusuku" footer) so what the sender
 *      sees in the preview matches what the recipient sees on click.
 *
 * Usage
 * -----
 *   <SEOHead {...buildVendorSeoProps(vendor, {
 *     canonicalPath: `/shop/${id}`,
 *     vendorType: 'retailer',
 *   })} />
 *
 *   const text = buildVendorShareText(vendor, { url, products: featured });
 *   navigator.clipboard.writeText(text);
 */

import { buildBrandSeoDescription } from "./SEOHead";
import { COUNTRY_NAME, formatCurrency, SITE_ORIGIN } from "../../config/market";
import { APP_STORE_URL, PLAY_STORE_URL } from "../../config/appStores";
import {
  PRICE_ON_REQUEST_LABEL,
  isQuoteOnly,
  publishedPrice,
} from "../../utils/priceOnRequest";

// Bare host (no scheme) for compact attribution, e.g. "lusuku.ug"
const SITE_HOST = SITE_ORIGIN.replace(/^https?:\/\//, "");

/**
 * Map a vendor record (retailer / restaurant / healthcare / brand) onto the
 * `brand`-shaped object that SEOHead.buildBrandJsonLd already understands.
 *
 * SEOHead was originally built for the B2B `Brand` model, which carries
 * `tradingName`, `tagline`, `legalName`, etc. Plain retailer/restaurant
 * vendors don't have all those fields yet, so we synthesise the shape from
 * whatever is present and let SEOHead's existing logic do the rest.
 *
 * Anything missing simply falls through to SEOHead's Lusuku default — that's
 * the contract: no custom branding ⇒ generic Lusuku preview, no broken UI.
 */
function vendorAsBrand(vendor) {
  if (!vendor || typeof vendor !== "object") return null;
  return {
    name: vendor.name,
    tradingName: vendor.tradingName,
    legalName: vendor.legalName,
    tagline: vendor.tagline,
    description: vendor.description,
    address: vendor.address,
    imageUrl: vendor.imageUrl || vendor.logoUrl,
    bannerUrl: vendor.bannerUrl,
    shopBannerUrl: vendor.shopBannerUrl,
    // brandType is used for keyword extraction; vendorType is a sensible proxy
    // when the vendor record itself doesn't carry one.
    brandType: vendor.brandType || vendor.vendorType,
  };
}

/**
 * Build the prop bag for <SEOHead /> from any vendor record.
 *
 * Preference order for the OG image (the social-card thumbnail):
 *   1. Vendor banner — landscape, looks great as a 1200×630 card.
 *   2. Vendor logo — fallback for vendors who only uploaded a logo.
 *   3. Lusuku logo — final fallback handled inside SEOHead itself.
 *
 * @param {object|null} vendor                        - Vendor record (any tier)
 * @param {object}      opts
 * @param {string}      opts.canonicalPath            - `/shop/:id`, etc.
 * @param {string}      [opts.vendorType]             - For default copy ("restaurant" → "Order from …")
 * @returns {object}                                  - Spread directly onto <SEOHead />
 */
export function buildVendorSeoProps(vendor, { canonicalPath, vendorType } = {}) {
  const brand = vendorAsBrand(vendor);

  // No vendor yet (still loading) — return a Lusuku-defaulted bag so the
  // preview is never broken, just generic until data lands.
  if (!brand) {
    return {
      title: defaultTitleFor(vendorType),
      description: defaultDescriptionFor(vendorType),
      canonicalPath,
    };
  }

  const displayName = brand.tradingName || brand.name || defaultTitleFor(vendorType);
  const description = brand.tagline || brand.description
    ? buildBrandSeoDescription(brand)
    : defaultDescriptionFor(vendorType, displayName);

  // Banner is preferred for the social card because Twitter/WhatsApp/Slack
  // render summary_large_image at 2:1 — a landscape banner fills it; a
  // square logo gets letterboxed. Logo is the safe fallback.
  const imageUrl = brand.bannerUrl || brand.shopBannerUrl || brand.imageUrl || undefined;

  return {
    title: displayName,
    searchTitle: storefrontSearchTitle(vendorType, brand.tradingName || brand.name),
    description,
    canonicalPath,
    imageUrl,        // undefined ⇒ SEOHead falls back to the Lusuku logo
    type: "website",
    brand,           // triggers Organization JSON-LD with the VENDOR as the org
  };
}

/**
 * A storefront's title in search results, before " | Lusuku". People look a
 * restaurant up as "<name> menu" or "<name> delivery", so the title says what
 * the page holds; social cards keep the name alone.
 *
 * MIRRORS storefrontSearchTitle in
 * Lusuku_Dashboard_Node-Server/services/seo/catalogSeo.js, which the edge
 * function serves to crawlers before this page has loaded. Both sides pin
 * STOREFRONT_TITLE_VECTORS (src/utils/__tests__/seoPaths.test.js here); change
 * them together, or the title Google indexes after rendering stops matching
 * the one it read first.
 *
 * @param {string} vendorType  "restaurant" gets the menu wording; every other
 *                             storefront (brand, retailer, healthcare) the shop one
 * @param {string} name        the name a shopper knows the vendor by
 */
export function storefrontSearchTitle(vendorType, name) {
  const n = String(name || "").trim();
  if (!n) return "";
  return vendorType === "restaurant" ? `${n}: menu and delivery` : `${n}: order online with delivery`;
}

function defaultTitleFor(vendorType) {
  switch (vendorType) {
    case "restaurant":
      return "Restaurant";
    case "healthcare":
      return "Healthcare provider";
    case "retailer":
    case "wholesaler":
      return "Vendor shop";
    default:
      return "Shop";
  }
}

function defaultDescriptionFor(vendorType, name) {
  const who = name || "this vendor";
  switch (vendorType) {
    case "restaurant":
      return `Order from ${who} on Lusuku. Fast delivery across ${COUNTRY_NAME}.`;
    case "healthcare":
      return `Order from ${who} on Lusuku — verified healthcare provider, delivery across ${COUNTRY_NAME}.`;
    case "retailer":
    case "wholesaler":
    default:
      return `Shop ${who} on Lusuku. Fast delivery across ${COUNTRY_NAME}.`;
  }
}

/* ─────────────────────────────────────────────────────────────────────────
 * Share-text composition
 * ─────────────────────────────────────────────────────────────────────────
 * These produce WhatsApp / clipboard payloads that mirror the OG card:
 * vendor first, products in the middle, then a Lusuku footer.
 *
 * The previous version dedicated six lines at the bottom to "Download
 * Lusuku" plus full Play Store / App Store URLs. That overshadowed the
 * vendor, but dropping the install links entirely was a mistake — the
 * apps are how recipients actually transact, so they earn a spot in the
 * footer. The compromise: one attribution line + one compact "Get the
 * apps" line containing both store URLs. Two lines, not six, and clearly
 * subordinate to the vendor block above.
 *
 * NO EMOJI, ANYWHERE IN THIS PAYLOAD. The text reaches WhatsApp through a
 * wa.me deep link, and on the desktop client every astral-plane character
 * (all emoji) came out as "\uFFFD" while the plain characters around it
 * survived (Sep 26 2026, screenshotted). The characters below are all in
 * the Basic Multilingual Plane, which every client decodes the same way.
 *
 * The rule is shorter than 30 cells on purpose: WhatsApp wrapped a
 * 30-cell one onto a second line in a phone-width bubble, which read as a
 * stray dash under the rule.
 */

const POWERED_BY = `Powered by Lusuku · ${SITE_HOST}`;
/* One store per line: WhatsApp wraps a long URL mid-token, and two of them on
   one line came out as four ragged fragments with the "· iOS:" label stranded
   between them (screenshotted 26 Sep 2026). */
const APP_LINKS_LINE = `Get the apps\nAndroid: ${PLAY_STORE_URL}\niOS: ${APP_STORE_URL}`;
const RULE = "─".repeat(20);
const DEFAULT_BULLET = "•";

function fmtUgx(amount) {
  if (amount == null || Number.isNaN(Number(amount))) return "";
  return formatCurrency(amount);
}

/**
 * What one product's price says in a SHARE MESSAGE.
 *
 * WHY THIS EXISTS. Both share builders below read `p.price` straight out and
 * formatted whatever came back, so a quote-only product was shared as "USh 0"
 * while the OG card attached to the very same WhatsApp message correctly said
 * "Price on request" (og-inject prints that label for `priceOnRequest` and
 * otherwise only prints a price above zero). The sender saw one thing in the
 * preview and typed another in the body.
 *
 * The label is the SAME STRING the storefronts and the OG card use, so a shopper
 * who reads the message, opens the card and then lands on the page is told the
 * same thing three times.
 *
 * An unpriced product deliberately contributes NOTHING rather than "Price
 * unavailable": in a shared message that reads as a fault in Lusuku rather than
 * a fact about the item, and the recipient can see the real state on the page.
 * That also preserves the old behaviour for a product with no price field.
 */
function sharePriceFor(product) {
  if (isQuoteOnly(product)) return PRICE_ON_REQUEST_LABEL;
  const value = publishedPrice(product);
  return value === null ? "" : fmtUgx(value);
}

function lineForProduct(p, bullet) {
  const name = p?.name || "Item";
  const price = sharePriceFor(p);
  const dots = ".".repeat(Math.max(3, 35 - (name.length + price.length)));
  return `${bullet} ${name} ${dots} ${price}`.trimEnd();
}

/**
 * Vendor catalogue share — used by "Copy shop link" and "Share on WhatsApp"
 * actions in each shop page's share menu.
 *
 * @param {object} vendor                          - Vendor record
 * @param {object} opts
 * @param {string} opts.url                        - Public storefront URL
 * @param {Array}  [opts.products=[]]              - Up to 5 featured products
 * @param {string} [opts.bullet='•']               - Per-line bullet (BMP only, see above)
 * @param {string} [opts.headerLabel='Shop']       - 'Shop' / 'Menu' / 'Pharmacy'
 */
export function buildVendorShareText(vendor, {
  url,
  products = [],
  bullet = DEFAULT_BULLET,
  headerLabel = "Shop",
} = {}) {
  const name = vendor?.tradingName || vendor?.name || "Lusuku vendor";
  const today = new Date().toLocaleDateString("en-US", {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  const items = products.slice(0, 5);
  const itemBlock = items.length
    ? `${items.map((p) => lineForProduct(p, bullet)).join("\n")}\n\n${RULE}\n\n`
    : "";

  return (
    `${name} ${headerLabel}\n` +
    `${today}\n` +
    `${RULE}\n\n` +
    itemBlock +
    `View and order:\n${url}\n\n` +
    `${POWERED_BY}\n` +
    APP_LINKS_LINE
  );
}

/* ─────────────────────────────────────────────────────────────────────────
 * Share-link cache-buster
 * ─────────────────────────────────────────────────────────────────────────
 * WhatsApp / Slack / Twitter / FB scrapers cache the OG preview per-URL
 * for ~7-30 days with no public invalidation API. The Facebook Sharing
 * Debugger only clears Meta's web cache — WhatsApp uses a separate cache
 * pool that ignores it. So a vendor URL shared once before the og-inject
 * edge function existed (or before the vendor uploaded a banner) stays
 * stuck on its stale "no preview" card until the entry naturally expires.
 *
 * Tying a `?v=YYYYMMDD` suffix to `vendor.updatedAt` solves three problems
 * in one stamp:
 *   1. Every fresh share from our app produces a URL the scraper has never
 *      seen, so it re-fetches and renders the vendor banner immediately.
 *   2. When a vendor changes their banner / logo / name, updatedAt bumps,
 *      the suffix changes, and previews re-scrape automatically — no
 *      manual cache flush.
 *   3. Multiple shares within the same day produce the same URL, so the
 *      scraper's positive cache hit rate stays high (we're invalidating
 *      precisely, not nuking).
 *
 * THE NO-OP THIS USED TO BE. The paragraph above assumes `vendor.updatedAt`
 * exists. It does not. Neither the restaurant model nor the
 * retailer/wholesaler model sets `timestamps: true`, so a live restaurant
 * record comes back with `createdAt` and no `updatedAt` at all (verified
 * against GET /restaurants/restaurant-profile/:id). Every call therefore hit
 * the early return and this helper had never appended a stamp to a single
 * share in production, which is the worst possible outcome: the FIRST scrape
 * a URL ever receives is the one that sticks, and for most vendors that
 * scrape happened before they had uploaded a logo. The generic Lusuku card
 * then outlives the branding it was supposed to be replaced by.
 *
 * So: fall back to an ISO-week bucket. Not `createdAt`, which is fixed
 * forever and would invalidate nothing. A week is the right grain against a
 * 7-30 day scraper cache: shares within the same week still collide on one
 * URL (cache hits stay high), and a vendor who uploads a logo today has
 * their previews re-scraped within days rather than never. The mobile apps'
 * shareMessage.js already carries this same fallback; keep the two in step.
 *
 * The real fix is upstream, and it is small: add `timestamps: true` to the
 * restaurant and retailer/wholesaler models and project `updatedAt` on the
 * read paths. Then the precise branch takes over on its own and this
 * fallback quietly stops being reached.
 */
function isoWeekBucket(now = new Date()) {
  // Thursday of the current week decides the ISO year, which is what stops
  // the bucket flapping across a new-year boundary.
  const d = new Date(
    Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate())
  );
  d.setUTCDate(d.getUTCDate() + 4 - (d.getUTCDay() || 7));
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  const week = Math.ceil(((d - yearStart) / 86400000 + 1) / 7);
  return `${d.getUTCFullYear()}w${String(week).padStart(2, "0")}`;
}

/* THE SHARE-CARD POLICY STAMP.
 *
 * The `v=` suffix above re-scrapes a URL when the VENDOR changes. It does
 * nothing when the CARD changes, because the card is composed server-side and
 * a vendor record that has not been touched keeps producing the same stamp.
 * So the day the card's own composition changed (the logo now leads instead of
 * a dish — see ogController.getRestaurantOgCard), every link already in a
 * WhatsApp thread, and every fresh share of an untouched vendor, would have
 * gone on serving the old picture out of the scraper's cache for another
 * 7-30 days, and the change would have looked like it had not shipped.
 *
 * Bumping this constant changes every share URL exactly once, which is what
 * forces the estate to re-scrape. Bump it ONLY when the card itself changes;
 * it is not a general cache flush, and each bump costs every vendor's warm
 * preview cache.
 *
 *   c2  logo led the card
 *   c3  26 Sep 2026: the listing cover (card image, else cover dish) leads,
 *       logo only as the fallback (ogController.pickShareCardImage)
 *   c4  same day: c3 went out on the web ~30 min before the backend that
 *       decides the picture, so WhatsApp scraped c3 links with the logo and
 *       cached them. A stamp is burned the moment a scraper sees it; ship
 *       the backend FIRST next time, then the stamp.
 *   c5  2 Oct 2026: not a card change. lusuku.ug was paused by Netlify on
 *       22 Sep, so every storefront link pasted into WhatsApp in that window
 *       was scraped against the "site paused" page and cached on the sender's
 *       phone as NO preview. WhatsApp never re-fetches a URL it has judged, so
 *       the only cure is a URL it has not seen. The apps had drifted to c2;
 *       all three clients now emit c5. */
const CARD_POLICY = "c5";

export function withVendorCacheBuster(url, vendor) {
  if (!url) return url;
  const sep = url.includes("?") ? "&" : "?";
  const updatedAt = vendor?.updatedAt;
  if (updatedAt) {
    const stamp = new Date(updatedAt);
    if (!Number.isNaN(stamp.getTime())) {
      const v = stamp.toISOString().slice(0, 10).replace(/-/g, "");
      return `${url}${sep}v=${v}${CARD_POLICY}`;
    }
  }
  return `${url}${sep}v=${isoWeekBucket()}${CARD_POLICY}`;
}

/* ─────────────────────────────────────────────────────────────────────────
 * The picture on a restaurant's share card, as the CLIENT resolves it
 * ─────────────────────────────────────────────────────────────────────────
 * The crawler's card is decided server-side by ogController.pickShareCardImage.
 * Every place on the web that shows a vendor "what people will see" (the
 * listing page's share sheet, the shop page's share menu) has to resolve the
 * same picture in the same order, or the preview lies. This is that order,
 * in one place:
 *   1. the uploaded 1440x900 card image        (photograph, fills the frame)
 *   2. the pinned or first cover dish           (photograph, fills the frame)
 *   3. the logo                                 (a mark, padded, never cropped)
 *   4. the banner                               (last resort, padded)
 * Change the server's order and change this in the same commit.
 */
const firstUrl = (...values) =>
  values.find((v) => typeof v === "string" && v.trim()) || "";

/**
 * The cover dish's photo for a restaurant: the pinned dish if it is still an
 * enabled dish with a photo, else the first such dish. Mirrors coverDishOf()
 * on AllRestaurantsPage and the `covers` facet in ogController.
 */
export function coverDishImageOf(restaurant, sections) {
  const dishes = (sections || [])
    .flatMap((section) => section?.products || [])
    .filter((dish) => dish && dish.enabled !== false && dish.image);
  const pinnedId = restaurant?.listingHero?.productId
    ? String(restaurant.listingHero.productId)
    : null;
  const pinned = pinnedId
    ? dishes.find((dish) => String(dish._id) === pinnedId)
    : null;
  return (pinned || dishes[0] || {}).image || "";
}

/**
 * @returns {{ image: string, fit: "cover" | "pad" }} the picture the card
 *   leads with and the Cloudinary preset family it wants (ogShareCardCover
 *   for a photograph, ogShareCard for a mark).
 */
export function shareCardImageFor(restaurant, coverDish) {
  const photo = firstUrl(restaurant?.cardImageUrl, coverDish);
  if (photo) return { image: photo, fit: "cover" };
  const mark = firstUrl(
    restaurant?.imageUrl,
    restaurant?.shopBannerUrl,
    restaurant?.bannerUrl
  );
  return { image: mark, fit: "pad" };
}

/**
 * Single-product share — used by the share button on each product card.
 *
 * @param {object} product                   - The product
 * @param {object} opts
 * @param {string} opts.url                  - Storefront (or product) URL
 * @param {object} [opts.vendor]             - For "{product} — {vendor}" attribution
 */
export function buildProductShareText(product, { url, vendor } = {}) {
  const productName = product?.name || "Product";
  const vendorTail = vendor?.name ? ` from ${vendor.name}` : "";
  /* Same rule as the catalogue lines: never a confident zero, and never a
     number the OG card in the same message contradicts. */
  const price = sharePriceFor(product);
  return (
    `${productName}${vendorTail}\n` +
    (price ? `${price}\n\n` : "\n") +
    `View and order:\n${url}\n\n` +
    `${POWERED_BY}\n` +
    APP_LINKS_LINE
  );
}
